import { useEffect, useState } from 'react';
import { APP_NAME, isNative, openExternal, platform } from '../lib/platform';
import { useServerInfo } from '../lib/serverInfo';
import { IonPage } from './ionic';

/** The native app's build number and ID; null on the web, and until they are known. */
function useAppInfo(): { build: number; id: string } | null {
  const [info, setInfo] = useState<{ build: number; id: string } | null>(null);
  useEffect(() => {
    if (!isNative) return;
    void import('@capacitor/app')
      .then(({ App }) => App.getInfo())
      .then((app) => setInfo({ build: Number(app.build), id: app.id }))
      .catch(() => {});
  }, []);
  return info;
}

/**
 * Whether this app is older than the oldest build the server still works
 * with (MIN_IOS_BUILD, MIN_ANDROID_BUILD), and so must be updated before
 * anything else. Never on the web, which is always the server's own.
 */
export function useUpdateNeeded(): { needed: boolean; appId: string | null } {
  const { info } = useServerInfo();
  const app = useAppInfo();
  if (!app || !Number.isFinite(app.build)) return { needed: false, appId: null };
  const oldest = (platform === 'ios' ? info?.minBuild?.ios : info?.minBuild?.android) ?? 0;
  return { needed: app.build < oldest, appId: app.id };
}

/** In place of every screen, in an app too old to work: what to do, and the way to the app's page in its own store. */
export function UpdateGate({ appId }: { appId: string | null }) {
  const stores = useServerInfo().info?.stores;
  // Each app names and opens only its own store.
  const ios = platform === 'ios';
  const store = ios ? (stores?.appStore ?? null) : (stores?.googlePlay ?? (appId ? `https://play.google.com/store/apps/details?id=${encodeURIComponent(appId)}` : null));
  const storeName = ios ? 'the App Store' : 'Google Play';
  return (
    <IonPage>
      <div className="screen screen--center update-gate" data-testid="update-gate">
        <img src={`${import.meta.env.BASE_URL}icon.svg`} alt="" width={56} height={56} />
        <h1>Update {APP_NAME}</h1>
        <p className="muted">This version of the app no longer works with {APP_NAME}. Update it to keep hosting and joining meetings.</p>
        {store ? (
          <button className="btn btn--primary btn--block" onClick={() => openExternal(store)}>
            Open {storeName}
          </button>
        ) : (
          <p className="muted">
            Open {storeName} and update {APP_NAME} there.
          </p>
        )}
      </div>
    </IonPage>
  );
}
